// Phase 9.1.B — Client portal fetch wrapper.
//
// Same shape as apps/web/src/lib/api.ts but targets the /portal/auth/*
// surface. Access JWT lives in memory only (NOT localStorage — XSS would
// lift it). httpOnly `merchd_portal_refresh` cookie carries the rotating
// 30-day refresh token; `credentials: "include"` sends it.
//
// On 401, calls /portal/auth/refresh once + retries. If refresh fails, the
// AuthProvider's onLogout handler clears state + pushes to /login.

import type { RefreshOutcome } from "./session-refresh";

let getAccessToken: (() => string | null) = () => null;
let refreshToken: () => Promise<RefreshOutcome> = async () => ({ kind: "signed_out" });
let onLogout: () => void = () => {};

export function bindAuth(args: {
  getAccessToken: () => string | null;
  refreshToken: () => Promise<RefreshOutcome>;
  onLogout: () => void;
}): void {
  getAccessToken = args.getAccessToken;
  refreshToken = args.refreshToken;
  onLogout = args.onLogout;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions {
  /** Skip auto-refresh on 401 — used for login + refresh calls themselves. */
  skipAuthRefresh?: boolean;
  /** Skip Authorization header — used for public routes (signup, login, verify). */
  skipAuth?: boolean;
}

async function rawFetch(
  input: RequestInfo,
  init: RequestInit,
): Promise<Response> {
  return fetch(input, {
    credentials: "include",
    ...init,
  });
}

/**
 * The access token ran out (401): renew it. Only a definitive "signed out"
 * from the server signs the person out; a deploy or a blip that outlasts the
 * retries throws a 503 and KEEPS the session (stay signed in, 2026-10-22 —
 * lib/session-refresh.ts).
 */
async function renewedToken(): Promise<string> {
  const outcome = await refreshToken();
  if (outcome.kind === "ok") return outcome.accessToken;
  if (outcome.kind === "signed_out") {
    onLogout();
    throw new ApiError(401, { error: "session_expired" }, "session expired");
  }
  throw new ApiError(
    503,
    { error: outcome.message ?? "Can't reach the server right now — you're still signed in. Try again in a moment." },
    "server unavailable",
  );
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & ApiOptions = {},
): Promise<T> {
  const { skipAuthRefresh, skipAuth, ...rest } = init;

  const headers = new Headers(rest.headers);
  if (rest.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!skipAuth) {
    const tok = getAccessToken();
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
  }

  let res = await rawFetch(path, { ...rest, headers });

  if (res.status === 401 && !skipAuthRefresh && !skipAuth) {
    const newTok = await renewedToken();
    const retryHeaders = new Headers(headers);
    retryHeaders.set("Authorization", `Bearer ${newTok}`);
    res = await rawFetch(path, { ...rest, headers: retryHeaders });
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // body may be empty/text
    }
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("Content-Type") ?? "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}
